import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { retentionDocApi } from "./axios";
import {
  DataI,
  ServiceResponseI,
  PropsRetentionDocumentI,
  RetentionDocumentI,
} from "../../interfaces";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";

const getRetentionDocuments = async ({
  pagination,
  sorting,
  globalFilter,
  dateFrom,
  dateTo,
  dateField,
}: PropsRetentionDocumentI): Promise<
  ServiceResponseI<DataI<RetentionDocumentI>>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("page", (pagination!.pageIndex + 1).toString());
    params.append("limit", pagination!.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    params.append("dateField", dateField ?? "");
    if (dateFrom) params.append("dateFrom", dateFrom);
    if (dateTo) params.append("dateTo", dateTo);

    const { data } = await retentionDocApi.get<
      ServiceResponseI<DataI<RetentionDocumentI>>
    >("", {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const deleteRetentionDocument = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await retentionDocApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      {
        headers: { Authorization: validateToken },
      },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

/**
 * Sincroniza "Comprobantes de retención" del SRI para un año/mes — reusa
 * la misma consulta de "Comprobantes recibidos" con tipoComprobante="6"
 * (ver ReceivedDocumentService.sincronizarRetenciones), así que el
 * payload es año/mes igual que en recibidos, no un rango de fechas.
 */
export const syncRetenciones = async (payload: {
  ano: string;
  mes: string;
}) => {
  const t = ValidateCurrentToken();
  if (!t) throw new Error("TOKEN INVALIDO");
  const { data } = await retentionDocApi.post("/sync", payload, {
    headers: { Authorization: t },
    timeout: 900_000, // 15 min: mismo margen que el resto de los syncs del SRI
  });
  return data;
};

export const getRetentionDocumentPdf = async (id: string): Promise<Blob> => {
  const token = ValidateCurrentToken();
  if (!token) throw new Error("TOKEN INVALIDO");
  const { data } = await retentionDocApi.get(`/${id}/pdf`, {
    headers: { Authorization: token },
    responseType: "blob",
  });
  return data;
};


export const getRetentionDocumentXml = async (id: string): Promise<Blob> => {
  const token = ValidateCurrentToken();
  if (!token) throw new Error("TOKEN INVALIDO");
  const { data } = await retentionDocApi.get(`/${id}/xml`, {
    headers: { Authorization: token },
    responseType: "blob",
  });
  return data;
};

export interface ReconcileRetencionesRecibidasResultI {
  revisados: number;
  corregidos: number;
  sinCambio: number;
}

/**
 * "Corregir": el backend consulta el SRI por clave de acceso y completa
 * fecha/número de autorización de las recibidas que llegaron sin ella
 * (típico de las que entran por correo).
 */
export const reconcileRetentionDocuments = async (): Promise<
  ServiceResponseI<ReconcileRetencionesRecibidasResultI>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await retentionDocApi.post<
      ServiceResponseI<ReconcileRetencionesRecibidasResultI>
    >("/reconcile", {}, {
      headers: { Authorization: validateToken },
      timeout: 600_000,
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error)?.message ?? "Error");
  }
};

export const useRetentionDocuments = ({
  sorting,
  pagination,
  globalFilter,
  dateFrom,
  dateTo,
  dateField,
}: PropsRetentionDocumentI) => {
  const queryClient = useQueryClient();

  const retentionDocumentQuery = useQuery(
    [
      "retention-documents",
      { pagination, sorting, globalFilter, dateFrom, dateTo, dateField },
    ],
    () =>
      getRetentionDocuments({
        pagination,
        sorting,
        globalFilter,
        dateFrom,
        dateTo,
        dateField,
      }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  const deleteOneMutation = useMutation(
    (id: string) => deleteRetentionDocument(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["retention-documents"]);
      },
    },
  );

  const deleteBulkMutation = useMutation(
    (ids: string[]) =>
      Promise.all(ids.map((id) => deleteRetentionDocument(id))),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["retention-documents"]);
      },
    },
  );

  // Sync manual de "Comprobantes de retención" para un año/mes elegido
  // desde el modal. Al terminar, invalida la query de la tabla.
  const syncRetencionesMutation = useMutation(
    (payload: { ano: string; mes: string }) => syncRetenciones(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["retention-documents"]);
      },
    },
  );

  const reconcileMutation = useMutation(() => reconcileRetentionDocuments(), {
    onSuccess: () => {
      queryClient.invalidateQueries(["retention-documents"]);
    },
  });

  return {
    reconcileMutation,
    retentionDocumentQuery,
    deleteOneMutation,
    deleteBulkMutation,
    syncRetencionesMutation,
  };
};
